import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { professionLabel, repoUrl, siteUrl } from "../../../shared/constants";
import { CaseStatusPill, formatDateTime } from "./ui";

interface DeployCase {
  id: string;
  status: string;
  slug: string | null;
  profession: string;
  office_name: string | null;
  published_url: string | null;
  updated_at: string;
}

// No.14 運営: 実装・デプロイ管理。
// 制作中/公開済みの案件と、CLI(lp jobs)が更新するステータス・公開URLを閲覧する。
export function AdminDeployPage() {
  const [cases, setCases] = useState<DeployCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ cases: DeployCase[] }>("/api/admin/cases")
      .then((res) =>
        setCases(
          res.cases.filter(
            (c) => c.status === "in_production" || c.status === "published",
          ),
        ),
      )
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(
    () => ({
      inProduction: cases.filter((c) => c.status === "in_production").length,
      published: cases.filter((c) => c.status === "published").length,
    }),
    [cases],
  );

  return (
    <section>
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow">運営管理</div>
          <h1>実装・デプロイ管理</h1>
          <p className="muted">
            制作中・公開済みの案件と、CLI（lp jobs）が更新するステータス・公開URLを一覧します。
          </p>
        </div>
      </div>

      <div className="stat-grid">
        <div className="card stat">
          <div className="stat-label">制作中</div>
          <div className="stat-value">{loading ? "…" : stats.inProduction}</div>
          <div className="stat-label">コーディング / デプロイ待ち</div>
        </div>
        <div className="card stat">
          <div className="stat-label">公開済み</div>
          <div className="stat-value">{loading ? "…" : stats.published}</div>
          <div className="stat-label">公開完了</div>
        </div>
      </div>

      <div className="card alert-info">
        ステータスと公開URLは運営者ローカルの CLI（
        <code>lp jobs pull &lt;id&gt;</code> →{" "}
        <code>lp jobs complete &lt;id&gt; --url ...</code>
        ）で更新されます。この画面は閲覧専用です。
      </div>

      <div className="card">
        {error ? (
          <div className="alert alert-error">{error}</div>
        ) : loading ? (
          <p className="muted">読み込み中...</p>
        ) : cases.length === 0 ? (
          <p className="muted">制作中・公開済みの案件がありません。</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>事務所名</th>
                <th>士業種別</th>
                <th>ステータス</th>
                <th>リポジトリ</th>
                <th>公開URL / ドメイン</th>
                <th>最終更新</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => {
                const repo = repoUrl(c.slug);
                const site = c.published_url ?? siteUrl(c.slug);
                return (
                  <tr key={c.id}>
                    <td className="cell-strong">{c.office_name ?? "（未設定）"}</td>
                    <td>{professionLabel(c.profession)}</td>
                    <td>
                      <CaseStatusPill status={c.status} />
                    </td>
                    <td>
                      {repo ? (
                        <a href={repo} target="_blank" rel="noreferrer">
                          site-{c.slug}
                        </a>
                      ) : (
                        <span className="muted small">slug未割当</span>
                      )}
                    </td>
                    <td>
                      {site ? (
                        <a href={site} target="_blank" rel="noreferrer">
                          {site.replace(/^https?:\/\//, "")}
                        </a>
                      ) : (
                        <span className="muted small">未設定</span>
                      )}
                    </td>
                    <td>{formatDateTime(c.updated_at)}</td>
                    <td>
                      <Link className="btn btn-ghost btn-sm" to={`/admin/cases/${c.id}`}>
                        詳細
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
